import React, { useState, useMemo, useRef, useLayoutEffect, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fromJS } from "immutable";
import { ChevronsUpDown, CircleX, Pencil, PencilIcon, Plus, Search, X } from "lucide-react";
import { Checkbox, Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { colorCodes, ColorPicker } from "../board/components/TaskDetail/ColorPicker";
import InputDebounce from "./InputDebounce";
import { deleteLabel, saveLabel } from "../api/LabelApi";
import LabelAction from "../actions/LabelAction";
import ConfirmDialog from "../dialog/ConfirmDialog";

const _fetchedLabelsCache = new Set<string>();
import OctagonAlertIcon from "../icons/OctagonAlertIcon";
import TriangleAlertIcon from "../icons/TriangleAlertIcon";
import LayoutAction from "../actions/LayoutAction";
import useResizeObserver from "../useResizeObserver";
import { truncateText } from "../utils/TextUtil";

export const ChipLabels = ({ type, assignedLabels, showRemove, onRemove }) => {
  const [containerLabelRef, size]: any = useResizeObserver();

  const visibleCount = useMemo(() => {
    if (assignedLabels.size === 1) return 1;

    const gap = 4; // gap-1
    const estimateChipWidth = (text) => {
      if (type === "FORM") {
        return text.length * 7 + gap * 4 + (showRemove ? 16 : 0);
      }
      return text.length * 5 + gap * 4 + (showRemove ? 16 : 0);
    };

    let width = size.width;
    if (type === "INLINE") {
      width = width - 48; // right icon
    } else if (type === "FORM") {
      width = width - 48; // right icon
    } else if (type === "GRID") {
    }
    const viewAllWidth = estimateChipWidth(`+${assignedLabels.size}`);
    let used = 0;
    let count = 0;

    const assignedLabelsData = assignedLabels.toJS();

    for (let i = 0; i < assignedLabelsData.length; i++) {
      const chipWidth = estimateChipWidth(assignedLabelsData[i].displayName || "");
      const next = used + chipWidth + gap + viewAllWidth;
      if (next > width) break;
      used += chipWidth + gap;
      count++;
    }

    return count;
  }, [showRemove, type, assignedLabels, size.width]);

  const titleAssignedLabels = assignedLabels
    .toJS()
    .slice(visibleCount, assignedLabels.size)
    .map((item) => item.name)
    .join("\n");

  let chipClass = "px-2 py-0.5 text-[10px] font-medium leading-4";
  if (type === "INLINE") {
  } else if (type === "FORM") {
    chipClass = "px-2 py-1.5 text-xs font-medium leading-4";
  } else if (type === "GRID") {
  }

  return (
    <div ref={containerLabelRef} className="w-full flex flex-nowrap gap-1 overflow-hidden">
      {assignedLabels
        .toJS()
        .slice(0, visibleCount)
        .map((item) => {
          const { id, name, displayName, color } = item;
          const colorCode = colorCodes.find((cc) => cc.color === color)?.code;
          return (
            <div
              key={id}
              title={name}
              className={`shrink-0 flex items-center gap-1 rounded-full ${chipClass} cursor-pointer ${
                colorCode ? "" : "border border-gray-300"
              }`}
              style={{
                backgroundColor: colorCode || "#fff",
                color: colorCode ? "#fff" : "rgb(0, 0, 0)",
              }}
            >
              <div>{displayName}</div>
              {showRemove ? (
                <X
                  className="w-3 h-3"
                  style={{ color: colorCode ? "#fff" : "rgb(0, 0, 0)" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onRemove(id);
                  }}
                />
              ) : (
                <div></div>
              )}
            </div>
          );
        })}
      {visibleCount < assignedLabels.size && (
        <button
          title={titleAssignedLabels}
          className={`shrink-0 flex items-center gap-1 rounded-full ${chipClass} cursor-pointer bg-gray-100 text-gray-600`}
        >
          +{assignedLabels.size - visibleCount}
        </button>
      )}
    </div>
  );
};

interface LabelsSelectProps {
  projectId: string;
  contentType: string;
  type: "INLINE" | "FORM" | "GRID";
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  showRemove?: boolean;
}

const LabelsSelect = ({
  projectId,
  contentType,
  type,
  value,
  onChange,
  placeholder,
  showRemove = false,
}: LabelsSelectProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const popoverButtonRef = useRef(null);
  const popoverPanelRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(null);

  const [keywords, setKeywords] = useState("");
  const [showForm, setShowForm] = useState({
    show: false,
    id: "",
    name: "",
    color: "",
  });
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });

  const allLabels = useSelector((state: any) => state.labelReducer.get(contentType)) || fromJS([]);

  useEffect(() => {
    const cacheKey = `${projectId}_${contentType}`;
    if (!_fetchedLabelsCache.has(cacheKey)) {
      _fetchedLabelsCache.add(cacheKey);
      dispatch(LabelAction.getLabels(projectId, contentType));
    }
  }, [projectId, contentType, dispatch]);

  const handleSave = async () => {
    if (!showForm.show || !showForm.name || !showForm.color) return;
    setIsSaving(true);
    const formData: any = {
      name: showForm.name.trim(),
      color: showForm.color,
      type: contentType,
    };
    if (showForm.id) {
      formData.id = showForm.id;
    }
    const savedLabel = await saveLabel(projectId, formData);
    if (savedLabel && savedLabel.id) {
      onChange([...value, savedLabel.id]);
    }
    await dispatch(LabelAction.getLabels(projectId, contentType));
    setShowForm({
      show: false,
      id: "",
      name: "",
      color: "",
    });
    setIsSaving(false);
  };

  const handleDelete = (id) => {
    setShowConfirm({
      show: true,
      title: t("Delete label"),
      description: t("Are you sure you want to remove this label?"),
      alertIcon: <TriangleAlertIcon />,
      dangerBtn: true,
      type: "DELETE",
      data: id,
    });
  };

  const handleSubmitConfirm = () => {
    if (showConfirm.type === "DELETE") {
      deleteLabel(projectId, showConfirm.data).then(() => {
        dispatch(LabelAction.getLabels(projectId, contentType));
        dispatch(
          LayoutAction.setNotification({
            title: t("Delete label"),
            type: "success",
            message: t("Delete label successfully!"),
          })
        );
      });
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  const renderSearch = () => {
    return (
      <div className="w-full flex items-center">
        <Search className="w-4 h-4 text-gray-400" />
        <InputDebounce
          autoFocus
          className={`w-full h-6 px-2 placeholder:text-gray-400 text-sm focus:outline-none`}
          placeholder={t("Search...")}
          value={keywords}
          onChange={(value: string) => {
            setKeywords(value);
          }}
        />
      </div>
    );
  };

  const renderLabelList = () => {
    if (allLabels.size === 0) {
      return (
        <div className="flex items-center justify-center p-1 text-gray-400">{t("No options")}</div>
      );
    }
    const labelList = allLabels
      .toJS()
      .map((item) => ({ ...item, selected: value.includes(item.id) }))
      .sort((a, b) => Number(b.selected) - Number(a.selected));
    const lastSelectedIndex = labelList.map((u) => u.selected).lastIndexOf(true);
    const buttonWidth = popoverButtonRef.current?.offsetWidth || 0;
    return labelList
      .filter((item) =>
        keywords ? item.name.trim().toLowerCase().includes(keywords.trim().toLowerCase()) : true
      )
      .map((item, index) => {
        const { id, name, color } = item;
        const colorCode = colorCodes.find((cc) => cc.color === color)?.code;
        const showDevide = lastSelectedIndex === index && index < labelList.length - 1;
        return (
          <React.Fragment key={id}>
            <div className="flex items-center justify-between group p-1 rounded-md hover:bg-gray-100 transition-colors gap-2">
              <div
                className="w-full flex items-center gap-2 cursor-default"
                onClick={() => {
                  if (value.includes(id)) {
                    const newValues = value.filter((val) => val !== id);
                    onChange(newValues);
                  } else {
                    onChange([...value, id]);
                  }
                }}
              >
                <Checkbox
                  checked={value.includes(id)}
                  className="group block size-4 rounded border border-gray-300 bg-white data-checked:bg-blue-500"
                >
                  <svg
                    className="stroke-white opacity-0 group-data-checked:opacity-100"
                    viewBox="0 0 14 14"
                    fill="none"
                  >
                    <path
                      d="M3 8L6 11L11 3.5"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Checkbox>
                <span
                  title={name}
                  className="rounded-full px-2 py-1 text-[10px] font-medium leading-4 truncate"
                  style={{
                    backgroundColor: colorCode || "#fff",
                    color: colorCode ? "#fff" : "rgb(0, 0, 0)",
                    maxWidth: `${buttonWidth > 200 ? buttonWidth - 76 : 200}px`,
                  }}
                >
                  {name}
                </span>
              </div>
              <div className="flex items-center gap-1 ml-2">
                <button
                  type="button"
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 cursor-pointer"
                  onClick={() => {
                    handleDelete(id);
                  }}
                  title={t("Delete label")}
                >
                  <CircleX className="w-4 h-4 text-red-600" />
                </button>
                <button
                  type="button"
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 cursor-pointer"
                  onClick={() => {
                    setShowForm({
                      show: true,
                      id: id,
                      name: name,
                      color: color,
                    });
                  }}
                  title={t("Edit label")}
                >
                  <Pencil className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>
            {showDevide ? <div className="w-full my-1 border-b border-gray-300"></div> : null}
          </React.Fragment>
        );
      });
  };

  const renderAddOrEdit = () => {
    const colorCode = colorCodes.find((cc) => cc.color === showForm.color)?.code;
    if (!showForm.show) {
      return (
        <div
          className="mt-2 flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
          onClick={() => {
            setShowForm({
              show: true,
              id: "",
              name: "",
              color: "",
            });
          }}
        >
          <Plus className="w-4 h-4 text-gray-600" />
          <div className="text-sm font-medium text-gray-600">{t("Create new label")}</div>
        </div>
      );
    }
    return (
      <div className="mt-2 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="mt-1 text-sm font-medium text-gray-600">
            {showForm.id ? t("Edit label") : t("Create new label")}
          </div>
          <div>
            {showForm.name && showForm.color && (
              <span
                className="rounded-full px-2 py-1 text-[10px] font-medium"
                style={{
                  backgroundColor: colorCode || "#fff",
                  color: colorCode ? "#fff" : "rgb(0, 0, 0)",
                }}
              >
                {showForm.name}
              </span>
            )}
          </div>
        </div>
        <input
          autoFocus
          className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm outline-none"
          placeholder={t("Label name")}
          value={showForm.name}
          onChange={(e) => {
            setShowForm((prev) => ({ ...prev, name: e.target.value }));
          }}
        />
        <div className="w-full">
          <ColorPicker
            color={showForm.color}
            onSelectItem={(item: any) => {
              setShowForm((prev) => ({ ...prev, color: item.color }));
            }}
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          <div></div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
              onClick={() => {
                setShowForm({
                  show: false,
                  id: "",
                  name: "",
                  color: "",
                });
              }}
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              disabled={!(showForm.name && showForm.color) || isSaving}
              className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-500 disabled:bg-gray-300 disabled:text-gray-600"
              onClick={handleSave}
            >
              {isSaving ? t("Saving...") : showForm.id ? t("Update") : t("Add")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderTarget = (isOpen) => {
    const assignedLabels = allLabels
      .filter((item) => value.includes(item.get("id")))
      .map((item) => fromJS({ ...item.toJS(), displayName: truncateText(item.get("name")) }));
    if (type === "INLINE") {
      return (
        <div className="group w-full flex items-center justify-between rounded-md transition-colors duration-150 hover:bg-gray-50 flex-1 min-h-[28px] cursor-pointer">
          {value.length > 0 ? (
            <ChipLabels
              type={type}
              assignedLabels={assignedLabels}
              showRemove={showRemove || isOpen}
              onRemove={(id) => {
                const datas = value.filter((val) => val !== id);
                onChange(datas);
              }}
            />
          ) : (
            <div className="w-full text-left font-medium text-xs leading-4 text-gray-400">
              {placeholder}
            </div>
          )}
          <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <PencilIcon className="w-3 h-3 text-gray-400 mr-2" />
          </div>
        </div>
      );
    } else if (type === "FORM") {
      return (
        <div
          className={`w-full h-9.5 flex items-center justify-between overflow-hidden gap-2 px-3 cursor-default rounded-lg ${
            isOpen ? "ring-4 ring-blue-100 border border-blue-600" : "border border-gray-200"
          }`}
        >
          {value.length > 0 ? (
            <ChipLabels
              type={type}
              assignedLabels={assignedLabels}
              showRemove={showRemove || isOpen}
              onRemove={(id) => {
                const datas = value.filter((val) => val !== id);
                onChange(datas);
              }}
            />
          ) : (
            <div className="flex items-center flex-nowrap overflow-hidden gap-2">
              <div className="text-sm leading-4 text-gray-500 truncate">{placeholder}</div>
            </div>
          )}
          <div className="shrink-0 flex items-center flex-nowrap gap-4">
            <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400" />
          </div>
        </div>
      );
    } else if (type === "GRID") {
      return (
        <div className="w-full cursor-pointer">
          {value.length > 0 ? (
            <ChipLabels
              type={type}
              assignedLabels={assignedLabels}
              showRemove={showRemove || isOpen}
              onRemove={(id) => {
                const datas = value.filter((val) => val !== id);
                onChange(datas);
              }}
            />
          ) : (
            <div className="w-full text-left leading-4 text-gray-500 cursor-default">
              {placeholder}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  let popoverPanelClassName = "";
  if (type === "INLINE") {
    popoverPanelClassName = "-mt-6.5";
  } else if (type === "FORM") {
    popoverPanelClassName = "mt-2";
  } else if (type === "GRID") {
    popoverPanelClassName = "mt-1";
  }

  return (
    <>
      <Popover className="w-full">
        {({ open }) => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useLayoutEffect(() => {
            if (!open) return;
            if (!popoverButtonRef.current || !popoverPanelRef.current) return;
            const buttonWidth = popoverButtonRef.current.offsetWidth;
            const panelNaturalWidth = popoverPanelRef.current.offsetWidth;
            setPanelWidth(Math.max(buttonWidth, panelNaturalWidth));
          }, [open]);

          return (
            <>
              <PopoverButton
                ref={popoverButtonRef}
                as="div"
                onClick={(e) => {
                  if (open) {
                    e.preventDefault();
                  }
                }}
              >
                {renderTarget(open)}
              </PopoverButton>
              <PopoverPanel
                ref={popoverPanelRef}
                anchor="bottom start"
                style={{ width: panelWidth ?? "auto" }}
                className={`z-9999 p-2 bg-white rounded-md border border-gray-300 shadow-xl ${popoverPanelClassName}`}
              >
                <div>
                  {renderSearch()}
                  <div className="w-full my-1 border-b border-gray-200"></div>
                  <div className="w-full max-h-60 overflow-y-auto">{renderLabelList()}</div>
                  <div className="w-full my-1 border-b border-gray-200"></div>
                  {renderAddOrEdit()}
                </div>
              </PopoverPanel>
            </>
          );
        }}
      </Popover>
      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={showConfirm.alertIcon || <OctagonAlertIcon />}
        dangerBtn={showConfirm.dangerBtn}
        onSubmit={handleSubmitConfirm}
        onClose={() => {
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
    </>
  );
};

export default LabelsSelect;
